/**
 * The Mail AREA half of the KnowIt adapter: `mail.items.*`, `mail.todos.*`, `mail.insights`,
 * `mail.settings.*`, `mail.imap.*`, `mail.intake.*`, `mail.sync.now`, `mail.overview`.
 *
 * Same bearer-injected transport as `knowitMail.ts` (the mailbox CONNECTIONS, which stay there).
 * A raw `fetch` from a scene arrives anonymous — every call rides the adapter.
 */
import type {
  MailAlertLevel,
  MailCard,
  MailDetail,
  MailImapAccount,
  MailImapInput,
  MailImapProbe,
  MailInsight,
  MailLane,
  MailListPage,
  MailOverview,
  MailPasteResult,
  MailPreference,
  MailRefineResult,
  MailRefineScope,
  MailRule,
  MailRuleDraft,
  MailRuleHit,
  MailRuleTest,
  MailRuleVocabulary,
  MailSettings,
  MailSettingsPatch,
  MailSyncReport,
  MailTodo,
  MailTodoStatus,
} from "~/api/mailReadingTypes";

export interface MailReadingTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

export interface MailListOptions {
  lane?: MailLane;
  q?: string;
  /** One kind, or several comma-separated — the pills. */
  kind?: string;
  offset?: number;
  limit?: number;
}

export interface MailReadingApi {
  overview(): Promise<MailOverview>;
  items: {
    list(options?: MailListOptions): Promise<MailListPage>;
    get(id: string): Promise<MailDetail>;
    /** state: seen | handled | archived | snoozed | open. `hours` or `until` for a snooze. */
    mark(
      id: string,
      state: "seen" | "handled" | "archived" | "snoozed" | "open",
      options?: { hours?: number; until?: string; todosToo?: boolean },
    ): Promise<MailCard>;
    reread(id: string): Promise<{ card: MailCard; queued: boolean }>;
    remove(id: string): Promise<void>;
    /**
     * "Not important for me" (2026-10-17): archive this mail and, unless scope is "none", teach the reader a
     * rule — by sender address, sender domain or kind — that quiets every later reading it matches.
     */
    refine(id: string, scope: MailRefineScope, note?: string): Promise<MailRefineResult>;
  };
  /** The rules the person taught ("not important for me"), and forgetting one (its readings come back). */
  prefs: {
    list(): Promise<MailPreference[]>;
    remove(id: string): Promise<{ restored: number }>;
  };
  /** Inbound rules (2026-10-17): conditions over what comes in → notify, change, close, file a tile, run a flow. */
  rules: {
    list(): Promise<{ rules: MailRule[]; vocabulary: MailRuleVocabulary }>;
    save(draft: MailRuleDraft): Promise<MailRule>;
    remove(id: string): Promise<void>;
    reorder(ids: string[]): Promise<MailRule[]>;
    /** Dry run against recent mail: a saved rule by id, or a draft. Nothing is changed. */
    test(draft: Partial<MailRuleDraft> & { id?: string; limit?: number }): Promise<MailRuleTest>;
    hits(options?: { itemId?: string; ruleId?: string; limit?: number }): Promise<MailRuleHit[]>;
  };
  todos: {
    list(status?: MailTodoStatus | "all"): Promise<{ todos: MailTodo[]; canFile: boolean }>;
    set(id: string, status: MailTodoStatus): Promise<MailTodo>;
    file(
      id: string,
      projectId?: string,
    ): Promise<{ todo: MailTodo; workItem: { id: string; key: string } }>;
  };
  insights(): Promise<MailInsight[]>;
  settings: {
    get(): Promise<MailSettings>;
    set(patch: MailSettingsPatch): Promise<MailSettings>;
    rotateIntake(): Promise<{ token: string; url: string }>;
    revokeIntake(): Promise<void>;
  };
  imap: {
    test(input: MailImapInput): Promise<MailImapProbe>;
    add(input: MailImapInput): Promise<MailImapAccount>;
    remove(id: string): Promise<void>;
    pause(id: string, paused: boolean): Promise<MailImapAccount>;
  };
  intake: {
    paste(text: string, hints?: { subject?: string; from?: string }): Promise<MailPasteResult>;
  };
  syncNow(): Promise<MailSyncReport>;
  /** Escape hatch for a verb the typed surface has not grown yet. */
  call(method: string, body?: Record<string, unknown>): Promise<unknown>;
}

type Envelope<T> = { data: T };

export function createKnowItMailReading(transport: MailReadingTransport): MailReadingApi {
  const post = <T>(method: string, body: Record<string, unknown> = {}) =>
    transport.post(method, body) as Promise<T>;
  const data = async <T>(method: string, body: Record<string, unknown> = {}) =>
    (await post<Envelope<T>>(method, body)).data;
  return {
    overview: () => data<MailOverview>("mail.overview"),
    items: {
      list: (options = {}) => post<MailListPage>("mail.items.list", { ...options }),
      get: (id) => data<MailDetail>("mail.items.get", { id }),
      mark: (id, state, options = {}) =>
        data<MailCard>("mail.items.mark", { id, state, ...options }),
      reread: async (id) => {
        const r = await post<{ data: MailCard; queued: boolean }>("mail.items.reread", { id });
        return { card: r.data, queued: r.queued };
      },
      remove: async (id) => {
        await post("mail.items.remove", { id });
      },
      refine: async (id, scope, note) => {
        const r = await post<{
          data: MailCard;
          preference: MailPreference | null;
          quieted: number;
        }>("mail.items.refine", note ? { id, scope, note } : { id, scope });
        return { card: r.data, preference: r.preference, quieted: r.quieted };
      },
    },
    prefs: {
      list: () => data<MailPreference[]>("mail.prefs.list"),
      remove: async (id) => {
        const r = await post<{ restored: number }>("mail.prefs.remove", { id });
        return { restored: r.restored };
      },
    },
    rules: {
      list: async () => {
        const r = await post<{ data: MailRule[]; vocabulary: MailRuleVocabulary }>(
          "mail.rules.list",
        );
        return { rules: r.data, vocabulary: r.vocabulary };
      },
      save: (draft) => data<MailRule>("mail.rules.save", { ...draft }),
      remove: async (id) => {
        await post("mail.rules.remove", { id });
      },
      reorder: (ids) => data<MailRule[]>("mail.rules.reorder", { ids }),
      test: (draft) => data<MailRuleTest>("mail.rules.test", { ...draft }),
      hits: (options = {}) => data<MailRuleHit[]>("mail.rules.hits", { ...options }),
    },
    todos: {
      list: async (status = "open") => {
        const r = await post<{ data: MailTodo[]; canFile: boolean }>("mail.todos.list", { status });
        return { todos: r.data, canFile: r.canFile };
      },
      set: (id, status) => data<MailTodo>("mail.todos.set", { id, status }),
      file: async (id, projectId) => {
        const r = await post<{ data: MailTodo; workItem: { id: string; key: string } }>(
          "mail.todos.file",
          projectId ? { id, projectId } : { id },
        );
        return { todo: r.data, workItem: r.workItem };
      },
    },
    insights: () => data<MailInsight[]>("mail.insights"),
    settings: {
      get: () => data<MailSettings>("mail.settings.get"),
      set: (patch) => data<MailSettings>("mail.settings.set", { ...patch }),
      rotateIntake: () => data<{ token: string; url: string }>("mail.settings.intake.rotate"),
      revokeIntake: async () => {
        await post("mail.settings.intake.revoke");
      },
    },
    imap: {
      test: (input) => data<MailImapProbe>("mail.imap.test", { ...input }),
      add: (input) => data<MailImapAccount>("mail.imap.add", { ...input }),
      remove: async (id) => {
        await post("mail.imap.remove", { id });
      },
      pause: (id, paused) => data<MailImapAccount>("mail.imap.pause", { id, paused }),
    },
    intake: {
      paste: async (text, hints = {}) => {
        const r = await post<{ data: MailCard; created: boolean; queued: boolean }>(
          "mail.intake.paste",
          { text, ...hints },
        );
        return { card: r.data, created: r.created, queued: r.queued };
      },
    },
    syncNow: () => data<MailSyncReport>("mail.sync.now"),
    call: (method, body = {}) => post(method, body),
  };
}

export type { MailAlertLevel };
